import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Inject,
  Input,
  PLATFORM_ID,
  ViewChild,
} from '@angular/core';
import {
  BehaviorSubject,
  Observable,
  Subject,
  map,
  shareReplay,
  startWith,
  take,
  takeUntil,
} from 'rxjs';

import {
  ApiService,
  Currency,
  FeaturedListing,
  Hostel,
} from '../services/api/api.service';
import { LocationService } from '@app/location.service';
import {
  CommonModule,
  NgOptimizedImage,
  isPlatformServer,
} from '@angular/common';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { HostelSearchParams, SearchService } from '@app/search.service';
import { RouterModule } from '@angular/router';
import { LocaleService } from '@app/locale.service';
import { CdnPipe } from '../cdn.pipe';

@Component({
  selector: 'app-card',
  templateUrl: './card.component.html',
  styleUrls: ['./card.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    AngularSvgIconModule,
    NgOptimizedImage,
    RouterModule,
    CdnPipe,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CardComponent {
  @Input()
  public hostel: FeaturedListing;
  @Input()
  public skeleton = false;
  @Input()
  public currency: Currency | null;
  @Input()
  public i: number;

  @ViewChild('img', { static: false }) imgEl: ElementRef;

  public distance?: number;
  public showLocation = false;
  public hostel$!: Observable<Hostel>;
  public ready$ = new BehaviorSubject(false);
  public searchParams: Observable<HostelSearchParams>;

  private destroyed = new Subject<void>();

  constructor(
    private api: ApiService,
    private locationService: LocationService,
    private searchService: SearchService,
    private localeService: LocaleService,
    @Inject(PLATFORM_ID) private platformId: string
  ) {}

  get isServer() {
    return isPlatformServer(this.platformId);
  }

  get loaded() {
    return this.imgEl?.nativeElement.complete;
  }

  ngOnInit() {
    this.searchParams = this.searchService.searchParams$;
    // can optimize this by returning everything from search/featured and passign entire json to card,
    // or returning very little from both of those and fetching everything here

    if (this.hostel?._id) {
      this.hostel$ = this.api.getPropertyRef(this.hostel._id).pipe(
        map((resp) => {
          this.ready$.next(true);
          // const location = this.locationService.getLocation();
          const location: any | undefined = undefined;
          let distance;
          if (location) {
            const propertyCoords = resp.address.geoLocation;
            distance = this.calcDistance(
              location?.coords.latitude,
              location?.coords.longitude,
              propertyCoords[0],
              propertyCoords[1]
            );
            this.distance = Math.round(distance! * 1000) / 1000;
          }

          return resp;
        }),
        take(1),
        takeUntil(this.destroyed),
        startWith(this.hostel),
        shareReplay({
          bufferSize: 1,
          refCount: false,
        })
      );
    }
  }

  ngOnDestroy() {
    this.destroyed.next();
    this.destroyed.complete();
  }

  private calcDistance(lat1: number, lon1: number, lat2: number, lon2: number) {
    var R = 6371; // km
    var dLat = this.toRad(lat2 - lat1);
    var dLon = this.toRad(lon2 - lon1);
    var lat1 = this.toRad(lat1);
    var lat2 = this.toRad(lat2);

    var a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.sin(dLon / 2) * Math.sin(dLon / 2) * Math.cos(lat1) * Math.cos(lat2);
    var c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    var d = R * c;

    return d;
  }

  // Converts numeric degrees to radians
  private toRad(value: number) {
    return (value * Math.PI) / 180;
  }
}
